import type { CSSProperties, RefObject } from 'react';
import { forwardRef, useRef } from 'react';
import styled, { css } from 'styled-components';
import type { GoogleAnalyticsEventParameter } from '@elfsight/lib-google-analytics';
import type { ContentDirection } from '@elfsight/widget-form-builder/widget';
import type { WidgetBackgroundProps } from '@elfsight/widget-ui';
import { WidgetBackground } from '@elfsight/widget-ui';
import {
  customScrollbarMixin,
  getAutoOrCustomSize
} from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import type { BlockProps, GoogleAnalytics } from '../types';
import { Layout } from '../types';
import { BannerProvider } from './bannerProvider';
import type { BlockComponents } from './blocks';
import { blockComponents, filterBlockProps } from './blocks';
import { MediaLayoutWrapper } from './MediaLayoutWrapper';
import { useFonts, useOuterMargin } from './utils';

export type BuilderContentAlignment = CSSProperties['alignContent'];

export type BuilderBlockProps = {
  blocks?: BlockProps[];
  widgetId: string;
  bannerBackground: WidgetBackgroundProps;
  stretchBackground?: boolean;
  style?: CSSProperties;
  components?: BlockComponents;
  bannerWidth?: number;
  bannerHeight?: WidgetBackgroundProps['height'];
  contentDirection?: ContentDirection;
  contentAlignment?: BuilderContentAlignment;
  contentHorizontalPadding?: number;
  contentVerticalPadding?: number | [number, number];
  contentSpacing?: number;
  contentStyle?: CSSProperties;
  contentRef?: RefObject<HTMLDivElement>;
  backgroundContentStyle?: CSSProperties;
  mobileView: boolean;
  googleAnalytics?: GoogleAnalytics;
  googleAnalyticsExtraParameters?: GoogleAnalyticsEventParameter;
  // TODO: find better solution
  adaptiveMedia?: boolean;
  getMediaHeightSetter?: (mediaId: string) => (height: number) => void;
};

export const Builder = forwardRef<HTMLDivElement, BuilderBlockProps>(
  (
    {
      blocks,
      bannerBackground,
      googleAnalytics,
      googleAnalyticsExtraParameters,
      stretchBackground = false,
      style,
      components = blockComponents,
      bannerWidth,
      bannerHeight,
      contentDirection = 'column',
      contentAlignment = 'flex-start',
      contentHorizontalPadding = 40,
      contentVerticalPadding = 40,
      contentSpacing = 20,
      contentStyle,
      contentRef,
      backgroundContentStyle,
      mobileView,
      widgetId,
      adaptiveMedia,
      getMediaHeightSetter
    },
    forwardingRef
  ) => {
    const backgroundRef = useRef<HTMLDivElement | null>(null);
    const mergedRef = useMergedRef(forwardingRef, backgroundRef);

    const isColumn = contentDirection === 'column';
    const borderRadius = bannerBackground?.borderRadius;

    const outerMargin = useOuterMargin({
      backgroundRef,
      blocks,
      bannerWidth,
      contentHorizontalPadding,
      stretchBackground
    });

    useFonts(blocks);

    const anySideMediaBlock = blocks?.some((block) => {
      if (!('layout' in block) || !block.layout || mobileView || !isColumn) {
        return false;
      }

      return [
        Layout.LeftMedia,
        Layout.RightMedia,
        Layout.LeftBanner,
        Layout.RightBanner
      ].includes(block.layout);
    });

    const heightMapping = {
      background: !anySideMediaBlock ? bannerHeight : undefined,
      content: anySideMediaBlock ? getAutoOrCustomSize(bannerHeight) : undefined
    };

    return (
      <BannerProvider
        widgetId={widgetId}
        blocks={blocks}
        googleAnalytics={googleAnalytics}
        googleAnalyticsExtraParameters={googleAnalyticsExtraParameters}
        contentSpacing={contentSpacing}
        contentDirection={contentDirection}
        contentHorizontalPadding={contentHorizontalPadding}
        contentVerticalPadding={contentVerticalPadding}
        stretchBackground={stretchBackground}
        borderRadius={borderRadius}
        outerMargin={outerMargin}
        mobileView={mobileView}
      >
        <WidgetBackground
          {...bannerBackground}
          ref={mergedRef}
          width={stretchBackground ? bannerWidth : undefined}
          height={heightMapping['background']}
          horizontalPadding={contentHorizontalPadding}
          verticalPadding={contentVerticalPadding}
          style={{
            marginInline: 'auto',
            maxWidth: '100%',
            width: !stretchBackground ? bannerWidth : undefined,
            ...style,
            ...bannerBackground?.style
          }}
          contentStyle={backgroundContentStyle}
        >
          <MediaLayoutWrapper
            blocks={blocks}
            components={components}
            isColumn={isColumn}
            mobileView={mobileView}
            adaptiveMedia={adaptiveMedia}
            getMediaHeightSetter={getMediaHeightSetter}
          >
            {(blocksWithoutMedia) => (
              <Content
                _hasSideMediaBlocks={
                  blocksWithoutMedia?.length !== blocks?.length
                }
                ref={contentRef}
                style={{
                  height: heightMapping['content'],
                  display: isColumn ? 'block' : 'flex',
                  alignContent: contentAlignment,
                  ...contentStyle
                }}
              >
                {blocksWithoutMedia?.map((block) => {
                  const Component =
                    components[block.type as keyof typeof blockComponents];
                  const filteredBlockProps = filterBlockProps(block);

                  return <Component key={block.id} {...filteredBlockProps} />;
                })}
              </Content>
            )}
          </MediaLayoutWrapper>
        </WidgetBackground>
      </BannerProvider>
    );
  }
);

const Content = styled.div.attrs({
  className: 'BuilderBlock__Content'
})<{
  _hasSideMediaBlocks: boolean;
}>`
  height: 100%;

  ${({ _hasSideMediaBlocks }) =>
    _hasSideMediaBlocks &&
    css`
      flex: 1;
      overflow: hidden auto;
      ${customScrollbarMixin};
    `}
`;
